/**
 * This file contains all the startup tasks that are specific to running a window within
 * Finsemble under FEA (as opposed to a freestanding app).
 */
import { System } from "../../common/system";
import { FEAGlobals } from "../../common/Globals";
import { RouterClient } from "../routerClient";
import { WindowClient, getControllerInstance } from "../windowClient";
import { ConfigClient } from "../configClient";
import { LauncherClient } from "../launcherClient";
import { Logger } from "../logger";
import { WindowOptions } from "../../FEA";
import { SystemStartupData } from "./types";
import SystemManagerClient from "../../common/systemManagerClient";
import { FinsembleWindow } from "../../common/FinsembleWindow";
import { NotificationClient } from "../notificationClient";

type DockingManagerRegisterOverrides = {
	allowAutoArrange: boolean;
	snapping: boolean;
	tiling: boolean;
	tabbing: boolean;
	canGroup: boolean;
	canMinimize: boolean;
	canMaximize: boolean;
	overwriteStartDocked: boolean;
};

/**
 * This code sends document.title changes (and initial value) off to the window service.
 * The window service may then decide to use this new value for display in the windowTitleBar
 * (e.g. unless overridden in app config)
 *
 * We use polling because it is simple and reliable. There are too many ways permutations to
 * track for how a document's title may be changed (<title> tag including virtual dom, setting
 * document.title programatically, combinations of the two, etc).
 *
 * Electron event for future refactoring title management in window management:
 * https://github.com/electron/electron/blob/main/docs/api/browser-window.md#event-page-title-updated
 */
const monitorDocumentTitleChanges = () => {
	if (!System.isTitlebarWindow()) {
		// Initial title set (only if the title is present)
		if (!!document?.title) {
			WindowClient.setWindowTitlePrivate(document.title);
		}

		// Add a watcher for the title change
		setInterval(() => {
			if (document?.title && WindowClient.getWindowTitle() !== document.title)
				WindowClient.setWindowTitlePrivate(document.title);
		}, 200);
	}
};

/**
 * Listens for changes in the hash and persists the change to the url property, and then saves it.
 */
const listenForHashChanges = () => {
	// we should only do this in the view or in the window if it has no views
	if (System.isTitlebarWindow()) {
		return;
	}

	const myWindow = window.top ?? window;

	// get url on page load.
	const url = myWindow.location.href;
	FEAGlobals.finsembleWindow.updateOptions({ url });

	// There's no pushState event in the browser. This is a monkey patched solution that allows us to catch hash
	// changes. onhashchange doesn't fire when a site is loaded with a hash (e.g., salesforce).
	(function (history: any) {
		const { pushState } = history;
		history.pushState = function (state) {
			if (typeof history.onpushstate === "function") {
				history.onpushstate({ state });
			}
			pushState.apply(history, arguments);

			const updatedURL = myWindow.location.href;
			FEAGlobals.finsembleWindow.updateOptions({ url: updatedURL });
		};

		const { replaceState } = history;
		history.replaceState = function (state) {
			if (typeof history.onreplacestate === "function") {
				history.onreplacestate({ state });
			}
			replaceState.apply(history, arguments);

			const updatedURL = myWindow.location.href;
			FEAGlobals.finsembleWindow.updateOptions({ url: updatedURL });
		};
	})(window.history);

	window.addEventListener("hashchange", () => {
		const updatedURL = myWindow.location.href;
		FEAGlobals.finsembleWindow.updateOptions({ url: updatedURL });
	});
};

/**
 * Prevents the browser's default behavior of loading files/images if they're dropped anywhere in the window.
 * If a component has a drop area that _doesn't_ preventDefault, the image/file will still be loaded.
 * This only prevents bad behavior from happening when the user drops an image/file on part of the window that _isn't_ listening for drag/drop events (usually by accident).
 */
const preventUnintendedDropEvents = (): void => {
	const preventDefault = (e) => {
		e.preventDefault();
	};

	window.addEventListener("dragover", preventDefault, false);
	window.addEventListener("drop", preventDefault, false);
};

/**
 * If the user presses windows key + left or right it causes all kinds of abhorrent behavior.
 * This function captures these hotkeys and essentially prevents the behavior.
 */
const maybeRejectWindowsKeyResizes = async (): Promise<void> => {
	// Get the 'enableWindowsAeroSnap' variable from the window service config
	const { err, data } = await ConfigClient.getValue<Record<string, any>>("finsemble");
	if (err || !data) {
		Logger.system.error("maybeRejectWindowsKeyResizes(): Error fetching config", err);
	}

	// If true, will send router messages to have docking respond to windows aero snap.
	// Otherwise, will restore a window's prior state when those aero events occur.
	const { enableWindowsAeroSnap = false }: { enableWindowsAeroSnap: boolean } = data?.services?.windowService?.config;

	// Responds to key events here in order to send router messages and determine whether a system-bounds-changed event should occur.
	// Essentially, this is catching actions to allow Finsemble to respond to windows aero snap functionality.
	// While true resize will be disabled which will prevent disable aerosnap from working. This flag will only be true while the windows key is down.
	let preventResize = false;
	const onKeyUp = async (e: KeyboardEvent) => {
		// aero snap enabled case, windows key released
		if (enableWindowsAeroSnap) {
			// If the key being released is the windows key, send a router message to docking
			if (e.key === "Meta") {
				RouterClient.transmit("Finsemble.WindowService.WindowsKey", "up");
			}
		} else {
			// When aerosnap is disabled and the windows key released, windows will be able to be resized.
			// Otherwise users would not be able to manually resize windows.
			if (e.key === "Meta" && preventResize) {
				preventResize = false;
				System.Window.getCurrent().updateOptions({
					resizable: true,
				});
			}
		}
	};
	const onKeyDown = (e: KeyboardEvent) => {
		// aero snap enabled case, windows key down
		if (enableWindowsAeroSnap) {
			// If the key being pressed is the windows key, send a message to docking
			if (e.key === "Meta") {
				RouterClient.transmit("Finsemble.WindowService.WindowsKey", "down");
			}
		} else {
			// When aero snap is disabled and the windows key is down, disable resize on windows to prevent aerosnap from working.
			if (e.key === "Meta") {
				preventResize = true;
				System.Window.getCurrent().updateOptions({
					resizable: false,
				});
			}
		}
	};

	// For Win + D shortcut only keyDown event is fired without keyUp, cause the window is minimized.
	// Restore the correct window state after Win + D. (fix for KB26881)
	FEAGlobals.finsembleWindow.addEventListener("minimized", () => {
		if (preventResize) {
			System.Window.getCurrent().updateOptions({
				resizable: true,
			});
			preventResize = false;
		}
	});

	/**
	 * The browser's keyDown isn't capable of capturing keyChords if the first key pressed is the windows key.
	 */
	window.addEventListener("keyup", onKeyUp);
	window.addEventListener("keydown", onKeyDown);
};

/**
 * Intercepts CTRL-R within titlebar windows and then causes both the titlebar window and app contents window
 * (browser view) to reload.
 *
 * When users press CTRL-R, they expect their app content to reload. Without this task, that would not occur -
 * only the titlebar window would reload, appearing as if nothing had happened.
 */
const modifyReload = (): void => {
	if (System.isTitlebarWindow()) {
		// Chromium's built-in reloading for CTRL+R happens before a keypress or keyup event is received,
		// so we must use keydown.
		document.addEventListener("keydown", (e) => {
			if (e.ctrlKey && e.key.toLowerCase() === "r") {
				e.preventDefault();
				e.stopPropagation();
				FEAGlobals.finsembleWindow.reload();
			}
		});
	}
};

let startedRegistrationWithDocking: boolean = false;

let deregisterPlease: boolean = false;

/**
 * This function is critical if you don't want to keep references of windows in the LauncherService after they close. It simply notifies the LauncherService that the window is no longer dockable. It's invoked when the window is closed.
 * **NOTE:** If you are using the finsemble windowTitleBar component, you do not need to call this function.
 */
const deregisterWithDockingManager = async () => {
	if (startedRegistrationWithDocking) {
		deregisterPlease = true;
	}
	const { name } = FEAGlobals.finsembleWindow;
	RouterClient.transmit("DockingService.deregisterWindow", {
		name,
	});
};

/**
 * This function is critical if you want docking and snapping to work. It transmits a message to the LauncherService, which registers it as a dockable window.
 */
const registerWithDockingManager = async (options: WindowOptions) => {
	if (FEAGlobals.finsembleWindow.parentWindow) {
		Logger.system.debug("registerWithDockingManager ignore registration request if has a parent");
		return;
	}
	const { uuid, name } = FEAGlobals.finsembleWindow;
	startedRegistrationWithDocking = true;
	await RouterClient.query("DockingService.registerWindow", {
		name,
		uuid,
		options,
		windowType: "WebWindow",
	});
	startedRegistrationWithDocking = false;
	if (deregisterPlease) {
		deregisterWithDockingManager();
		deregisterPlease = false;
	}
	Logger.system.debug("WINDOW LIFECYCLE: Docking Registration complete.");
};

const maybeRegisterForDocking = async (myConfig: Record<string, any>): Promise<void> => {
	/**
	 * BrowserViews should never register with the window service, nor should
	 * windows that don't have a "component" entry in their config.
	 */
	if (System.isBrowserView() || !myConfig?.component) return;

	const windowServiceConfig = myConfig?.foreign?.services?.windowService;
	const anyAllowIsTrue =
		windowServiceConfig?.allowSnapping ||
		windowServiceConfig?.allowGrouping ||
		windowServiceConfig?.allowAutoArrange ||
		windowServiceConfig?.allowTabbing ||
		windowServiceConfig?.allowTiling ||
		windowServiceConfig?.allowMinimize ||
		windowServiceConfig?.allowMaximize;

	// default value in the Schema is `true`
	const manageWindowMovement = windowServiceConfig?.manageWindowMovement ?? true;
	const isDockable = myConfig?.window?.dockable;
	const FSBLHeader = myConfig?.foreign?.components?.["Window Manager"]?.FSBLHeader;

	// If we can manipulate window (allow*) OR manage movement OR window is dockable OR window has FSBLHeader then register for docking.
	const shouldRegisterDocking = anyAllowIsTrue || manageWindowMovement || isDockable || FSBLHeader;

	if (shouldRegisterDocking) {
		/**
		 * manageMovement dictates generically whether this is a window that the user can manipulate. This is true if
		 * manageWindowMovement is not set explicitly to `false`, or if there is an FSBLHeader (windowTitleBar) or if the window is explicitly dockable.
		 */
		const defaultValue = windowServiceConfig?.manageWindowMovement ?? (FSBLHeader || isDockable) ?? true;
		const registerMessage: DockingManagerRegisterOverrides = {
			allowAutoArrange: windowServiceConfig?.allowAutoArrange ?? defaultValue,
			// Ephemeral windows are not allowed to snap
			snapping: !myConfig?.window?.ephemeral && (windowServiceConfig?.allowSnapping ?? defaultValue),
			tiling: windowServiceConfig?.allowTiling ?? defaultValue,
			tabbing: windowServiceConfig?.allowTabbing ?? defaultValue,
			canGroup: windowServiceConfig?.allowGrouping ?? defaultValue,
			canMinimize: windowServiceConfig?.allowMinimize ?? myConfig?.component?.canMinimize ?? defaultValue,
			canMaximize: windowServiceConfig?.allowMaximize ?? myConfig?.component?.canMaximize ?? defaultValue,
			// Determines whether a dockable component should retrieve its state from memory, or start with default (config defined) options every time
			overwriteStartDocked: myConfig?.foreign?.services?.workspaceService?.global ?? false,
		};

		await registerWithDockingManager({
			...myConfig.window,
			...registerMessage,
		});

		// stack takes care of this too but doesn't work at startup or workspace switch so do again here
		FEAGlobals.finsembleWindow.addListener("setParent", () => {
			Logger.system.info("finsembleWindow setParent event: deregisterWithDockingManager");
			deregisterWithDockingManager();
		});
	}
};

/**
 * Both FinsembleWindow and WindowClient provide a static member that contains system options.
 * These must be initialized on startup.
 *
 * Note: these static members should be deprecated in favor of direct asynchronous calls to FSBL.System.getOptions().
 *
 * @param systemStartupData The initial options fetched from FSBL.System.getOptions()
 *
 */
const setInitialOptions = (systemStartupData: SystemStartupData) => {
	FEAGlobals.finsembleWindow.windowOptions = systemStartupData ?? {};
	const controller = getControllerInstance();
	controller.setStartupData(systemStartupData);
};

/**
 * Obtains the initial bounds and then saves them to the workspace
 */
export const cacheInitialBounds = async () => {
	// Don't run this function in the titlebar window because then updateOptions will be called twice
	// for the same overall window
	if (System.isTitlebarWindow()) return;

	const { err, data: bounds } = await FEAGlobals.finsembleWindow.getBounds();
	if (err || !bounds) {
		console.warn("Get bounds error.", err, "Window may not be registered with the window service");
		return;
	}

	// If the url is to be persisted then set it as an option so that the workspace will pick it up for future instantiations
	const { persistWindowState = false } =
		FEAGlobals.finsembleWindow.windowOptions?.customData?.foreign?.components?.["Window Manager"];
	const url = persistWindowState ? (window.top ?? window).location.toString() : undefined;

	await FEAGlobals.finsembleWindow.updateOptions({
		options: {
			defaultLeft: bounds.left,
			defaultTop: bounds.top,
			defaultWidth: bounds.width,
			defaultHeight: bounds.height,
			url,
		},
	});
};

/**
 * Communicates to the window service when this window is minimized and restored from a minimized state.
 */
const addMinimizedRestoreWatcher = () => {
	FEAGlobals.finsembleWindow.addEventListener("minimized", () => {
		const onMinimizedRestored = () => {
			RouterClient.transmit("DockingService.windowRestored", FEAGlobals.finsembleWindow.name);
			FEAGlobals.finsembleWindow.removeEventListener("restored", onMinimizedRestored);
		};

		RouterClient.query("DockingService.windowMinimized", {
			windowName: FEAGlobals.finsembleWindow.name,
		});
		FEAGlobals.finsembleWindow.addEventListener("restored", onMinimizedRestored);
	});
};

/**
 * The window service expects to receive heartbeats for every window that it spawns.
 */
const startHeartbeats = (windowName: string) => {
	setInterval(() => {
		RouterClient.transmit("Finsemble.heartbeat", {
			type: "component",
			windowName: windowName,
			componentType: "finsemble",
			isBrowserView: System.isBrowserView(),
		});
	}, 1000);
};

/**
 * This is only needed for deprecated LauncherClient group stuff which will soon be removed
 */
const getWindowGroups = (finsembleWindow: FinsembleWindow) => {
	// @deprecate all of this code
	// Get Group Updates (only if we are not in a service)
	if (finsembleWindow) {
		// Get Groups from Component State on Load
		const subscribeToGroupUpdates = async () => {
			await SystemManagerClient.waitForStartup("windowService");
			RouterClient.subscribe(`Finsemble.LauncherService.updateGroups.${finsembleWindow.name}`, (err, response) => {
				if (!Array.isArray(response.data)) return; // don't attempt to save the initial responder state.
				finsembleWindow.setComponentState({
					field: "finsemble:windowGroups",
					value: response.data,
				});
			});
		};
		finsembleWindow.getComponentState({ field: "finsemble:windowGroups" }, (err, groups) => {
			if (!err && groups) {
				return LauncherClient.addToGroups(
					{
						groupNames: groups,
					},
					subscribeToGroupUpdates
				);
			}
			subscribeToGroupUpdates();
		});
	}
};

/**
 * Overrides the browser's window.Notification so that notifications are instead sent
 * through Finsemble's notification system.
 */
const overrideBrowserNotify = async (windowName: string, myConfig: Record<string, any>, componentType?: string) => {
	const { data: applyNotificationOverrides } = await ConfigClient.getValue(
		"finsemble.servicesConfig.notifications.applyNotificationOverrides"
	);

	if (!applyNotificationOverrides) return;

	const source = myConfig?.component?.displayName ?? componentType ?? windowName;

	const notifyOverride = (title, opt) => {
		opt.finsembleSource = source;

		NotificationClient.notify(JSON.parse(JSON.stringify({ title, options: opt })));
	};

	const OldNotify = window.Notification;
	notifyOverride.requestPermission = OldNotify.requestPermission.bind(OldNotify);
	Object.defineProperty(notifyOverride, "permission", {
		get: function () {
			return OldNotify.permission;
		},
	});

	window.Notification = notifyOverride as any;
};

/**
 * Runs all local client side code necessary to participate in Finsemble's desktop.
 * @todo, remove clients from BaseClient and convert to singletons
 * @todo, move command channel stuff into WindowTitleBar - deprecate
 */
export const windowStartup = async ({
	windowName,
	componentType,
	finsembleWindow,
	systemStartupData,
}: {
	windowName: string;
	componentType?: string;
	finsembleWindow: FinsembleWindow;
	systemStartupData: SystemStartupData;
}): Promise<void> => {
	startHeartbeats(windowName);
	getWindowGroups(finsembleWindow);
	monitorDocumentTitleChanges();
	listenForHashChanges();
	preventUnintendedDropEvents();
	maybeRejectWindowsKeyResizes();
	modifyReload();
	setInitialOptions(systemStartupData);
	addMinimizedRestoreWatcher();
	overrideBrowserNotify(windowName, systemStartupData.customData, componentType);
	await maybeRegisterForDocking(systemStartupData.customData);
	await cacheInitialBounds();
};
